// Minimal ECharts mount hook. Tree-shaken via the modular import path so
// the bundle only carries the chart types we actually use.

import { useEffect, useRef } from "preact/hooks";
import * as echarts from "echarts/core";
import { BarChart, HeatmapChart, LineChart, PieChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

echarts.use([
  BarChart,
  HeatmapChart,
  LineChart,
  PieChart,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

import type { EChartsCoreOption } from "echarts/core";

export const BRAND_PALETTE = ["#002da5", "#fab400", "#e6d2b4", "#6b7280", "#16213e"];

const BASE: EChartsCoreOption = {
  color: BRAND_PALETTE,
  textStyle: { fontFamily: "IBM Plex Sans, Inter, Arial" },
  grid: { top: 28, right: 16, bottom: 28, left: 48, containLabel: true },
};

export function useECharts(option: EChartsCoreOption | null) {
  const ref = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const inst = echarts.init(ref.current, undefined, { renderer: "canvas" });
    instanceRef.current = inst;
    const ro = new ResizeObserver(() => inst.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      inst.dispose();
      instanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    const inst = instanceRef.current;
    if (!inst || !option) return;
    inst.setOption({ ...BASE, ...option }, { notMerge: true });
  }, [option]);

  return ref;
}
